import { SxProps, Theme } from '@mui/material';
import { TEXT_SECONDARY } from '../../../styles/GlobalStyles';
import {
  CARD_SCROLLBAR_GAP_PX,
  EXPERIENCE_CARD_RESTING_LEFT_INSET_PX,
  EXPERIENCE_CARD_RESTING_RIGHT_INSET_PX,
} from '../CardListScrollbar.constants';

const APP_HEADER_HEIGHT_PX = 92;
const CONTENT_TOP_MARGIN_PX = 50;
const PAGE_SCROLL_TOP_PX = APP_HEADER_HEIGHT_PX + CONTENT_TOP_MARGIN_PX;

export const assignedStyles = {
  container: (theme: Theme) =>
    ({
      marginTop: `${CONTENT_TOP_MARGIN_PX}px`,
      // The Angular shell starts main content below its 92px header, so both offsets must be removed.
      height: `calc(100dvh - ${PAGE_SCROLL_TOP_PX}px)`,
      overflowY: 'auto',
      padding: theme.spacing(2),
      boxSizing: 'border-box',
    }) as SxProps<Theme>,

  tabsRow: (theme: Theme, hasSubscriptionContext: boolean) =>
    ({
      display: 'flex',
      alignItems: 'flex-start',
      flexWrap: hasSubscriptionContext ? 'wrap' : 'nowrap',
      gap: theme.spacing(2),
      // The 50px tab row centers 21px update text 14.5px down; its old top margin was 2px.
      marginTop: hasSubscriptionContext ? '-12.5px' : 0,
      marginBottom: theme.spacing(2),
      position: 'relative',
      // Draw behind the tabs so their blue selection indicator remains visible.
      '&::before': hasSubscriptionContext ? {
        content: '""',
        position: 'absolute',
        left: 0,
        right: `${EXPERIENCE_CARD_RESTING_RIGHT_INSET_PX}px`,
        bottom: 0,
        height: '1px',
        backgroundColor: theme.palette.divider,
        pointerEvents: 'none',
      } : undefined,
    }) as SxProps<Theme>,

  filtersRow: (theme: Theme) =>
    ({
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      flexWrap: 'wrap',
      marginLeft: 'auto',
    }) as SxProps<Theme>,

  summaryAndFiltersRow: (theme: Theme) =>
    ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: theme.spacing(3),
      marginBottom: theme.spacing(2),
      width: '100%',
      position: 'relative',
    }) as SxProps<Theme>,

  summaryHeaderPlacement: () =>
    ({
      marginLeft: `${EXPERIENCE_CARD_RESTING_LEFT_INSET_PX}px`,
    }) as SxProps<Theme>,

  summaryFiltersRow: (theme: Theme) =>
    ({
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'stretch',
      gap: theme.spacing(1.5),
      flexWrap: 'nowrap',
      position: 'absolute',
      right: `${EXPERIENCE_CARD_RESTING_RIGHT_INSET_PX}px`,
      top: '50%',
      transform: 'translateY(-50%)',
    }) as SxProps<Theme>,

  filterSelectContainer: () =>
    ({
      minWidth: '180px',
      maxWidth: '240px',
      width: '220px',
    }) as SxProps<Theme>,

  lastUpdatedRow: (theme: Theme) =>
    ({
      display: 'flex',
      alignItems: 'center',
      alignSelf: 'center',
      flexShrink: 1,
      minWidth: 0,
      maxWidth: '100%',
      marginLeft: 'auto',
      boxSizing: 'border-box',
      justifyContent: 'flex-end',
      gap: theme.spacing(1),
      paddingRight: `${EXPERIENCE_CARD_RESTING_RIGHT_INSET_PX}px`,
    }) as SxProps<Theme>,

  lastUpdatedText: (theme: Theme) =>
    ({
      fontSize: theme.typography.body2.fontSize,
      color: theme.palette.text.secondary,
    }) as SxProps<Theme>,

  refreshButton: (theme: Theme) =>
    ({
      color: theme.palette.primary.main,
      padding: 0,
      width: '20px',
      height: '20px',
      flexShrink: 0,
    }) as SxProps<Theme>,

  tabs: (hasSubscriptionContext: boolean) =>
    ({
      // Keep both labels and the notification badge visible; update controls can take another line.
      flex: hasSubscriptionContext ? '1 0 auto' : 1,
      minWidth: hasSubscriptionContext ? 'max-content' : 0,
      borderBottom: 1,
      borderColor: 'divider',
    }) as SxProps<Theme>,

  tab: (theme: Theme) =>
    ({
      fontSize: '20px',
      fontWeight: theme.typography.fontWeightMedium,
      outline: 'none !important',
      boxShadow: 'none !important',
      border: 'none !important',
      '&:focus, &:active, &:focus-visible': {
        outline: 'none !important',
        boxShadow: 'none !important',
        border: 'none !important',
      },
      '&.Mui-focusVisible': {
        outline: `2px solid ${theme.palette.primary.main} !important`,
        outlineOffset: '-2px',
      },
    }) as SxProps<Theme>,

  tabPanel: () =>
    ({
      overflowY: 'visible',
      paddingRight: 0,
    }) as SxProps<Theme>,

  assignExperienceText: (theme: Theme) =>
    ({
      marginBottom: theme.spacing(2),
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(9),
      color: TEXT_SECONDARY,
      fontSize: '18px',
      fontWeight: theme.typography.fontWeightRegular,
    }) as SxProps<Theme>,

  endedNotificationBadge: () =>
    ({
      display: 'inline-flex',
      alignItems: 'center',
      '& .MuiBadge-badge': {
        fontSize: '12px',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'static',
        marginLeft: '5px',
        transform: 'none',
        backgroundColor: 'rgb(255, 102, 51)',
      },
    }) as SxProps<Theme>,

  refreshIcon: () =>
    ({
      width: '20px',
      height: '20px',
      display: 'block',
      transform: 'translateY(2px)',
    }) as SxProps<Theme>,

  addExperienceButton: (theme: Theme) =>
    ({
      // Half the 56px button extends below the tabs without changing their height.
      alignSelf: 'flex-end',
      marginBottom: '-28px',
      width: '56px',
      height: '56px',
      minWidth: '56px',
      minHeight: '56px',
      padding: 0,
      color: theme.palette.common.white,
      backgroundColor: theme.palette.primary.main,
      borderRadius: '50%',
      boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)',
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
        boxShadow: '0 6px 14px rgba(0, 0, 0, 0.24)',
      },
    }) as SxProps<Theme>,

  addExperienceIcon: () =>
    ({
      width: '18px',
      height: '18px',
      display: 'block',
    }) as SxProps<Theme>,

  experiencesList: (theme: Theme) =>
    ({
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      gap: theme.spacing(1.5),
      paddingRight: `${CARD_SCROLLBAR_GAP_PX}px`,
      boxSizing: 'border-box',
    }) as SxProps<Theme>,

  emptyAssignedState: () =>
    ({
      display: 'flex',
      justifyContent: 'center',
      width: '100%',
    }) as SxProps<Theme>,

  emptyAssignedStateImage: () =>
    ({
      display: 'block',
      width: 'calc(100% - 16px)',
      height: 'auto',
      aspectRatio: '920 / 100',
      boxShadow: '3px 3px 6px rgba(0, 0, 0, 0.35)',
    }) as SxProps<Theme>,
};

export default assignedStyles;
